import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getProductionOrder, setProductionOrderKnife } from "../api/productionOrders";
import { listKnivesPaged } from "../api/knives";

const PAGE_LIMIT = 10;

export default function WorkOrderKnifeDialog({ workOrder, onClose, onSuccess }) {
  const [knifeId, setKnifeId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Rezervasyon parti seçimindeki gibi: 3 ayrı filtre + 10 satırlık sayfalı liste.
  const [filterDiameter, setFilterDiameter] = useState("");
  const [filterPlates, setFilterPlates] = useState("");
  const [filterMold, setFilterMold] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], page: 1, limit: PAGE_LIMIT, total: 0 });
  const [listLoading, setListLoading] = useState(false);

  // İş emrinin kayıtlı bıçağını ön seçim olarak al.
  useEffect(() => {
    let cancelled = false;
    setKnifeId("");
    setError("");
    getProductionOrder(Number(workOrder?.id))
      .then((detail) => { if (!cancelled) setKnifeId(detail?.knife_id ? String(detail.knife_id) : ""); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error?.message || "Bıçak bilgileri yüklenemedi"); });
    return () => { cancelled = true; };
  }, [workOrder?.id]);

  // Filtre değişince ilk sayfaya dön.
  useEffect(() => {
    setPage(1);
  }, [filterDiameter, filterPlates, filterMold]);

  const loadKnives = useCallback(async () => {
    setListLoading(true);
    setError("");
    try {
      const data = await listKnivesPaged({
        status: "active",
        page,
        limit: PAGE_LIMIT,
        diameter: filterDiameter.trim() || undefined,
        platesCount: filterPlates.trim() || undefined,
        moldType: filterMold.trim() || undefined,
      });
      setResult({
        items: data?.items || [],
        page: Number(data?.page || page),
        limit: Number(data?.limit || PAGE_LIMIT),
        total: Number(data?.total || 0),
      });
    } catch (e) {
      setError(e?.response?.data?.error?.message || "Bıçak listesi yüklenemedi");
      setResult({ items: [], page: 1, limit: PAGE_LIMIT, total: 0 });
    } finally {
      setListLoading(false);
    }
  }, [page, filterDiameter, filterPlates, filterMold]);

  useEffect(() => { loadKnives(); }, [loadKnives]);

  async function handleSave() {
    const selectedKnifeId = Number(knifeId);
    if (!Number.isInteger(selectedKnifeId) || selectedKnifeId <= 0) {
      alert("Bıçak seçin");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await setProductionOrderKnife(Number(workOrder?.id), selectedKnifeId);
      onSuccess?.();
    } catch (e) {
      setError(e?.response?.data?.error?.message || "Bıçak kaydedilemedi");
    } finally {
      setSaving(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(result.total / result.limit));

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 42, height: 42, borderRadius: 2.25, display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.24)", fontSize: 20 }}>◈</Box><Box><Typography variant="h6" fontWeight={800}>İş Emrine Bıçak Ata</Typography><Typography variant="body2" sx={{ opacity: 0.8 }}>İş emrinde kullanılacak bıçağı seçip planlama hesabına bağlayın.</Typography></Box></Stack>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc" }}>
        <Stack spacing={2}>
          {error ? <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert> : null}
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}><Stack spacing={0.5}>
            <Typography variant="body2"><b>İş Emri:</b> {workOrder?.order_no || "—"}</Typography>
            <Typography variant="body2"><b>Müşteri:</b> {workOrder?.customer_name || "—"}</Typography>
          </Stack></Paper>

          <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
            <Box sx={{ mb: 1.5 }}><Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#6366f1" }} /><Typography fontWeight={800}>Bıçak seçimi</Typography></Stack><Typography variant="body2" color="text.secondary">Ölçü, tabak sayısı ve kalıp tipine göre filtreleyip uygun bıçağı seçin.</Typography></Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mb: 1.5 }}>
              <TextField size="small" fullWidth label="Ölçü (cm)" placeholder="Ölçüye göre ara (ör. 23)" value={filterDiameter} onChange={(e) => setFilterDiameter(e.target.value)} disabled={saving} />
              <TextField size="small" fullWidth label="Tabak sayısı" placeholder="Tabak sayısına göre ara (ör. 4)" value={filterPlates} onChange={(e) => setFilterPlates(e.target.value)} disabled={saving} />
              <TextField size="small" fullWidth label="Kalıp tipi" placeholder="Kalıp tipine göre ara" value={filterMold} onChange={(e) => setFilterMold(e.target.value)} disabled={saving} />
            </Stack>

            {listLoading ? <LinearProgress sx={{ mb: 1, borderRadius: 1 }} /> : null}

            <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
              {result.items.length === 0 && !listLoading ? (
                <Box sx={{ display: "grid", placeItems: "center", py: 4, px: 3 }}>
                  <Typography color="text.secondary" variant="body2">Filtreye uygun bıçak bulunamadı.</Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {result.items.map((knife) => {
                    const selected = String(knife.id) === String(knifeId);
                    return (
                      <ListItemButton
                        key={knife.id}
                        divider
                        selected={selected}
                        onClick={() => setKnifeId(String(knife.id))}
                        disabled={saving}
                        sx={{ py: 1.2 }}
                      >
                        <ListItemText
                          primary={<Typography fontWeight={700}>{Number(knife.diameter_cm)} cm — {knife.plates_count} tabak — {knife.mold_type || "—"}</Typography>}
                          secondary={`Mt/Tabak: ${knife.mt_per_plate ?? "—"}`}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Paper>

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.25 }}>
              <Typography variant="caption" color="text.secondary">Toplam {result.total} bıçak · Sayfa {result.page} / {totalPages}</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" disabled={listLoading || result.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Önceki</Button>
                <Button size="small" disabled={listLoading || result.page >= totalPages} onClick={() => setPage((p) => p + 1)}>Sonraki</Button>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e7eaf3", justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary">* Bıçak seçimi, iş emrinin malzeme ihtiyacı hesabında kullanılır.</Typography>
        <Stack direction="row" spacing={1}><Button onClick={onClose} disabled={saving} color="inherit">Kapat</Button><Button onClick={handleSave} disabled={saving || !knifeId} variant="contained" sx={{ px: 2.5, borderRadius: 2, boxShadow: "0 8px 18px rgba(79, 70, 229, 0.24)" }}>{saving ? "Kaydediliyor..." : "Bıçağı Ata"}</Button></Stack>
      </DialogActions>
    </Dialog>
  );
}
